<template>
  <div style="width: 100%">
    <spinner v-if="loading" size="200" />

    <div v-else>
      <h1>Vos infos</h1>
      <p class="mt-4 mb-8">
        Pour modifier vos coordonnées, rendez-vous sur le chat !
      </p>
      <div class="flex items-start">
        <simplebar
          class="card mr-16 shadow-md"
          data-simplebar-auto-hide="false"
        >
          <div class="flex align-center">
            <h2>Gestion</h2>
          </div>
          <h4>Infos générales</h4>
          <InformationDisplay
            :text="account.company"
            label="Nom de l’entreprise"
          />
          <InformationDisplay
            :text="clientSince"
            label="Date de début de contrat*"
          />
          <InformationDisplay
            :text="engagedUntil"
            label="Date de fin de contrat"
          />
          <InformationDisplay
            :text="account.employees"
            label="Nombre de collaborateurs"
          />
          <p class="italic" style="font-size: 12px">
            *: Vous êtes engagé sur 12 mois, avec tacite reconduction. Le
            premier trimestre de votre contrat est sans engagement. Pour en
            savoir plus, rendez-vous sur nos
            <a
              target="_blank"
              style="font-size: 12px; text-decoration: underline"
              href="https://www.delicorner.co/cgv"
              >CGV</a
            >.
          </p>

          <h4>Infos personnelles</h4>
          <InformationDisplay :text="account.lastname" label="Votre nom" />
          <InformationDisplay :text="account.firstname" label="Votre prénom " />
          <InformationDisplay
            :text="account.email"
            label="Votre adresse mail"
          />
          <InformationDisplay :text="account.phone" label="Votre téléphone" />

          <h4>Infos comptables</h4>
          <template v-if="account.billing">
            <InformationDisplay
              :text="account.billing.name"
              label="Votre nom"
            />
            <InformationDisplay
              :text="account.billing.email"
              label="Votre adresse mail"
            />
            <InformationDisplay
              :text="account.billing.phone"
              label="Votre téléphone"
            />
          </template>

          <InformationDisplay
            :text="billingsAddresses[0].address"
            label="Adresse"
          />
          <InformationDisplay
            :text="billingsAddresses[0].zip"
            label="Code postal"
          />
          <InformationDisplay :text="billingsAddresses[0].city" label="Ville" />

          <h4 class="mb-2">Vos documents</h4>
          <div class="quoteContainer">
            <a
              v-for="(el, i) of quotes"
              :key="i"
              :href="
                'https://my.delicorner.co/UserBundle/uploads/SignedQuotation/' +
                el.file
              "
              style="width: 33%"
              target="_blank"
            >
              <v-document :text="el.name" class="mr-1" />
            </a>
          </div>
        </simplebar>

        <simplebar class="card shadow-md" data-simplebar-auto-hide="false">
          <div class="flex align-center"><h2>Livraison</h2></div>

          <div class="input-group searchBar">
            <div class="input-group-append">
              <div class="btn btn-secondary searchIcon" type="button">
                <i class="fa fa-search"></i>
              </div>
            </div>
            <input
              v-model="searchInput"
              class="form-control"
              placeholder="Rechercher"
              type="text"
            />
          </div>

          <div
            v-for="(el, i) in deliveryAddressesFilter"
            :key="i"
            class="addressContainer"
          >
            <div class="divider" />
            <div class="siteName">
              <p class="mr-1 label">Nom du site :</p>
              <p>{{ el.name }}</p>
            </div>
            <InformationDisplay :text="el.address" label="Adresse" />
            <InformationDisplay :text="el.zip" label="Code postal" />
            <InformationDisplay :text="el.city" label="Ville" />
            <InformationDisplay :text="el.contact" label="Contact sur place" />
            <InformationDisplay
              :text="el.phone"
              label="N° de téléphone du contact"
            />
          </div>
        </simplebar>
      </div>
    </div>
  </div>
</template>

<script>
import { apiCallRebuild, notifyErrorFetchRaw } from "@/helper";
import { API_ROUTES } from "@/Services/api.common.service";
import InformationDisplay from "@/views/Information/InformationDisplay";
import Moment from "moment";
import simplebar from "simplebar-vue";
import "simplebar/dist/simplebar.min.css";

export default {
  components: {
    InformationDisplay,
    simplebar,
  },
  data() {
    return {
      loading: true,
      deliveriesAddresses: [],
      billingsAddresses: [],
      quotes: [],
      searchInput: "",
      account: {},
    };
  },
  watch: { $route: "fetchData" },
  created() {
    this.fetchData();
  },

  computed: {
    deliveryAddressesFilter() {
      if (this.searchInput.length === 0) return this.deliveriesAddresses;
      else {
        const isAddressIn = (addressElement) => {
          for (const value in addressElement) {
            if (addressElement[value].includes(this.searchInput)) return true;
          }
          return false;
        };

        return this.deliveriesAddresses.filter(isAddressIn);
      }
    },
    clientSince() {
      if (!this.account.clientSince) {
        return "----";
      }
      return new Moment(new Date(this.account.clientSince)).format(
        "DD/MM/YYYY"
      );
    },
    engagedUntil() {
      if (!this.account.clientSince) {
        return "----";
      }
      return Moment(new Date(this.account.engagedUntil)).format("DD/MM/YYYY");
    },
  },
  methods: {
    async fetchData() {
      const notifyError = notifyErrorFetchRaw.bind(this);
      try {
        const apiRes = await apiCallRebuild(API_ROUTES.information);

        this.deliveriesAddresses = apiRes?.deliveriesAddresses?.addresses || [];
        this.quotes = apiRes?.quotes?.quotes || {};
        this.billingsAddresses = apiRes?.billingsAddresses?.addresses || [];
        this.account = apiRes?.account?.account || {};

        this.loading = false;
      } catch (e) {
        console.error(e);
        notifyError();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.divider {
  border: 0;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
}

h1 {
    font-size: 3.5em;
    margin-bottom: 40px;
}

h4 {
  color: var(--main-green);
  font-family: Avenir, sans-serif;
  font-size: 18px;
  font-weight: 900;
  padding-bottom: 4px;
  padding-top: 20px;
}

h2 {
  background: var(--dark-green);
  color: white;
  display: inline-block;
  font-size: 24px;
  margin: auto;
  padding: 4px 60px;
  text-align: center;
}

.card {
  background: white;
  height: 750px;
  overflow-x: hidden;
  padding: 25px;
  width: 50%;
}

.quoteContainer {
  display: flex;
  flex-wrap: wrap;

  & > a {
    margin-top: 8px;
    width: 100px;

    p {
      text-align: left !important;
    }
  }
}

.siteName {
  color: var(--main-green);
  display: flex;
  font-weight: 500;
  margin-top: 20px;

  h5 {
    font-weight: 900;
    margin-right: 10px;
  }
}

.quoteTitle {
  padding-bottom: 20px;
}

.searchBar {
  @apply mt-6;

  input,
  input:focus {
    border: var(--dark-green) 1px solid !important;
    box-shadow: none !important;
    outline: var(--dark-green) !important;
  }

  .searchIcon {
    background: var(--dark-green);
    border: none;

    &:hover {
      background: var(--dark-green);
      cursor: default;
    }
  }

  .fa-search {
    color: white;
  }
}
</style>
